import * as _ from "lodash-es";
import { getCurrentInstance, onUnmounted, toRef, watch } from "vue";
import { type Listener } from "./listener";

function usePropSync(props: Record<string, any>, key: string) {
	const instance = getCurrentInstance()!;
	if (instance == null) throw new Error("No Vue instance");

	const propRef = toRef(props, key);

	const keyUppercase = `${key[0].toUpperCase()}${key.slice(1)}`;
	const setFnName = `set${keyUppercase}`;
	const getFnName = `get${keyUppercase}`;

	const googleEventName = `${key.toLowerCase()}_changed`;
	const updateEventName = `update:${key}`;

	let stability = 0;
	let lastUpdateTime = Date.now();

	const i = setInterval(() => {
		if (stability === 0) return;

		if (Date.now() - lastUpdateTime > 10_000) {
			stability = 0;
		}
	}, 1_000);
	onUnmounted(() => clearInterval(i));

	let listener: google.maps.MapsEventListener | undefined;
	let object: Listener | undefined;

	function setListener(value: Listener) {
		object = value;
		bind();
	}

	function bind() {
		listener = object?.addListener(
			googleEventName,
			_.throttle(() => {
				if (object == null) return;

				stability = Math.max(stability - 1, -1);

				lastUpdateTime = Date.now();

				if (stability < 0) {
					const value = object[getFnName]();
					instance.emit(updateEventName, value);
					instance.emit(googleEventName);
				}
			}, 100)
		);
	}

	function unbind() {
		listener?.remove();
	}

	watch(
		propRef,
		(val) => {
			if (object == null || object[setFnName] == null || val === undefined) return;

			lastUpdateTime = Date.now();

			stability = Math.min(stability + 1, 1);
			if (stability > 0) {
				object[setFnName](val);
			}
		},
		{ immediate: true }
	);

	onUnmounted(unbind);

	return { setListener, unbind };
}

export const usePropsBinding = (props: Record<string, any>) => {
	const propsSync: ReturnType<typeof usePropSync>[] = [];

	for (const key in props) {
		if (Object.prototype.hasOwnProperty.call(props, key)) {
			propsSync.push(usePropSync(props, key));
		}
	}

	const bindProps = (object: Listener) => {
		unbindProps();

		for (const sync of propsSync) {
			sync.setListener(object);
		}
	};

	const unbindProps = () => {
		for (const sync of propsSync) {
			sync.unbind();
		}
	};

	return { bindProps };
};
